import React, { useState, useEffect, useCallback } from 'react'
import { Users, Loader2, AlertCircle, UserPlus } from 'lucide-react'
import { Card, Badge, Button } from '../ui'
import { Organization } from '../../services/organizationService'
import { userService, User } from '../../services/userService'
import InviteUserModal from './InviteUserModal'

interface OrganizationUsersProps {
  organization: Organization
}

const OrganizationUsers: React.FC<OrganizationUsersProps> = ({ organization }) => {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [inviteModalOpen, setInviteModalOpen] = useState(false)

  const loadUsers = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await userService.getUsers({ organizationId: organization.id, limit: 100 })
      setUsers(response.data?.users ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }, [organization.id])

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const statusVariant = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status?.toLowerCase()) {
      case 'active': return 'success'
      case 'inactive': return 'warning'
      case 'suspended': return 'error'
      default: return 'default'
    }
  }

  if (loading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <Loader2 className='h-6 w-6 animate-spin text-gray-400' />
      </div>
    )
  }

  if (error) {
    return (
      <div className='flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-lg p-4'>
        <AlertCircle className='h-4 w-4 flex-shrink-0' />
        <span className='text-sm'>{error}</span>
      </div>
    )
  }

  return (
    <>
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <Users className='h-4 w-4 text-gray-500' />
          <span className='text-sm font-medium text-gray-700'>
            {users.length} {users.length === 1 ? 'User' : 'Users'}
          </span>
        </div>
        <Button size='sm' onClick={() => setInviteModalOpen(true)}>
          <UserPlus className='h-3.5 w-3.5 mr-1.5' />
          Invite User
        </Button>
      </div>

      <Card variant='outlined' className='overflow-hidden p-0'>
        {users.length === 0 ? (
          <div className='py-10 text-center text-sm text-gray-500'>
            No users found for this organization.
          </div>
        ) : (
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Name
                </th>
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Email
                </th>
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Role
                </th>
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Last Login
                </th>
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Status
                </th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-100'>
              {users.map((user) => (
                <tr key={user.id} className='hover:bg-gray-50 transition-colors'>
                  <td className='px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap'>
                    {[user.firstName, user.lastName].filter(Boolean).join(' ') || '—'}
                  </td>
                  <td className='px-4 py-3 text-sm text-gray-600'>
                    {user.email}
                  </td>
                  <td className='px-4 py-3 text-sm text-gray-600 whitespace-nowrap'>
                    {user.roleName || '—'}
                  </td>
                  <td className='px-4 py-3 text-sm text-gray-500 whitespace-nowrap'>
                    {formatDate(user.lastLoginAt)}
                  </td>
                  <td className='px-4 py-3 whitespace-nowrap'>
                    <Badge variant={statusVariant(user.statusName)}>
                      {user.statusName || 'Unknown'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>

    <InviteUserModal
      isOpen={inviteModalOpen}
      onClose={() => setInviteModalOpen(false)}
      onInviteSent={() => {
        setInviteModalOpen(false)
        loadUsers()
      }}
      defaultOrganizationId={organization.id}
    />
    </>
  )
}

export default OrganizationUsers
